import { AwsSecretSchema, type AwsSecret, type KvpsDealerId } from '@org-avp/models';
import { EXHIBITION_STAGE } from 'utils/ui/stage';
import { user } from './user';
import { getOAuthClient, isAuthenticated, refreshTokens } from '.';
import { IStage } from 'data/stage';
import { sendApiRequest } from 'utils/network';
import { IntegrationType, integrationType } from 'utils/ui/app';

const avpDevicesTvmIdTokenEndpoint: Record<IStage, string> = {
  test: 'https://devices.develop.avp.tech/credentials/viaIdToken',
  dev: 'https://devices.develop.avp.tech/credentials/viaIdToken',
  prelive: 'https://devices.preprod.avp.tech/credentials/viaIdToken',
  prod: 'https://devices.avp.tech/credentials/viaIdToken',
};

export async function fetchCredentialsFromTvm(
  idToken: string,
  stage: IStage,
  dealerId: KvpsDealerId
): Promise<AwsSecret> {
  const tvmUrl = avpDevicesTvmIdTokenEndpoint[stage];
  const response = await fetch(`${tvmUrl}/${dealerId}`, { headers: { Authorization: `Bearer ${idToken}` } });
  if (!response.ok) {
    throw new Error(`Failed to fetch credentials: ${response.status}: ${response.statusText}`);
  }
  const json: unknown = await response.json();
  const credentials = AwsSecretSchema.safeParse(json);
  if (!credentials.success) {
    console.debug('Malformed credentials fetched from TVM', { error: credentials.error });
    throw new Error('Malformed backend data');
  }
  return credentials.data;
}

const pendingApiCredentials: Record<string, Promise<AwsSecret>> = {};

/**
 * Dealer-scoped device credentials through our `/avp-tvm` route, signed with the ID token. Works in
 * every host, including DCC, whose tokens come from the integrator callbacks. Concurrent calls for
 * one dealer share a request.
 */
export function fetchCredentialsViaApi(dealerId: string): Promise<AwsSecret> {
  if (Object.prototype.hasOwnProperty.call(pendingApiCredentials, dealerId)) {
    return pendingApiCredentials[dealerId];
  }
  const request = requestCredentialsViaApi(dealerId).finally(() => {
    delete pendingApiCredentials[dealerId];
  });
  pendingApiCredentials[dealerId] = request;
  return request;
}

async function requestCredentialsViaApi(dealerId: string): Promise<AwsSecret> {
  const response = await sendApiRequest(`/avp-tvm/${dealerId}`, { method: 'POST', idToken: true });
  const credentials = AwsSecretSchema.safeParse(response);
  if (!credentials.success) {
    throw new Error('Malformed backend data', { cause: { error: credentials.error, response } });
  }
  return credentials.data;
}

export async function tvmCredentialsProvider(): Promise<AwsSecret> {
  if (!EXHIBITION_STAGE) throw new Error('Stage not available');
  if (!user.dealerIdKvps) throw new Error('Dealer Id not available');

  // AVS-4508: a web component has no standalone sign-in on its host's origin.
  if (integrationType() === IntegrationType.WebComponent) return fetchCredentialsViaApi(user.dealerId);

  if (!isAuthenticated()) {
    const userClient = getOAuthClient();
    const currentUser = await userClient.getUser();
    if (!currentUser) throw new Error('User not available');
    const refreshed = await refreshTokens(userClient, currentUser);
    if (!refreshed) throw new Error('Unable to refresh tokens');
  }

  const idToken = user?.getIdToken();
  if (!idToken) throw new Error('Id Token not available');
  return await fetchCredentialsFromTvm(idToken, EXHIBITION_STAGE, user.dealerId as KvpsDealerId);
}
